import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, Button, Divider, ToggleButton, ToggleButtonGroup, Checkbox, FormControlLabel,
    Switch, TextField
} from '@mui/material';
import { InstitutionSettingsService } from '../../services/institutionSettings.service';
import { InstitutionService } from '../../services/institution.service';
import { WeekDay } from '../../interfaces/institutionSettings.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import UsageLimitsCard from './UsageLimitsCard';
import ModuleAccessCard from './ModuleAccessCard';
import ApprovalControlsCard from './ApprovalControlsCard';
import { helpWhy } from '../../help/why';

const WEEK_DAYS: { value: WeekDay; label: string }[] = [
    { value: 'MONDAY', label: 'Mon' },
    { value: 'TUESDAY', label: 'Tue' },
    { value: 'WEDNESDAY', label: 'Wed' },
    { value: 'THURSDAY', label: 'Thu' },
    { value: 'FRIDAY', label: 'Fri' },
    { value: 'SATURDAY', label: 'Sat' },
    { value: 'SUNDAY', label: 'Sun' },
];

const InstitutionSettingsPage: React.FC = () => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution();
    const [weekendDays, setWeekendDays] = useState<WeekDay[]>(['FRIDAY', 'SATURDAY']);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [twoFactorPolicy, setTwoFactorPolicy] = useState<'OPTIONAL' | 'REQUIRED'>('OPTIONAL');
    const [savingTwoFactor, setSavingTwoFactor] = useState(false);
    const [locationMode, setLocationMode] = useState<'OFF' | 'FLAG'>('OFF');
    const [thresholdMeters, setThresholdMeters] = useState<number>(100);
    const [savingLocation, setSavingLocation] = useState(false);
    // Company name: only admins can read/update the institution record, so the section stays
    // hidden (code === null) when that read is refused.
    const [companyName, setCompanyName] = useState('');
    const [companyCode, setCompanyCode] = useState<string | null>(null);
    const [companyAddress, setCompanyAddress] = useState('');
    const [companyTinBin, setCompanyTinBin] = useState('');
    const [savingName, setSavingName] = useState(false);

    useEffect(() => {
        const load = async () => {
            if (!institutionId) {
                setLoading(false);
                return;
            }
            try {
                const settings = await InstitutionSettingsService.getSettings(institutionId);
                if (settings?.weekendDays && settings.weekendDays.length > 0) {
                    setWeekendDays(settings.weekendDays);
                }
                if (settings?.twoFactorPolicy) {
                    setTwoFactorPolicy(settings.twoFactorPolicy);
                }
                if (settings?.attendanceLocationMode) {
                    setLocationMode(settings.attendanceLocationMode);
                }
                if (settings?.locationAccuracyThresholdMeters) {
                    setThresholdMeters(settings.locationAccuracyThresholdMeters);
                }
            } catch (error) {
                toast.showApiError(error, 'Failed to load institution settings');
            } finally {
                setLoading(false);
            }
        };
        load();
        if (institutionId) {
            Promise.resolve()
                .then(() => InstitutionService.getInstitutionById(institutionId))
                .then(inst => {
                    if (inst?.code) {
                        setCompanyName(inst.name ?? '');
                        setCompanyAddress(inst.address ?? '');
                        setCompanyTinBin(inst.tinBin ?? '');
                        setCompanyCode(inst.code);
                    }
                })
                .catch(() => setCompanyCode(null));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const handleSaveName = async () => {
        if (!institutionId || !companyCode) return;
        const trimmed = companyName.trim();
        if (!trimmed) {
            toast.showError('Company name is required');
            return;
        }
        try {
            setSavingName(true);
            // Null fields are left unchanged by the update, so only these fields move.
            await InstitutionService.updateInstitution(institutionId, {
                name: trimmed, code: companyCode, address: companyAddress.trim(), tinBin: companyTinBin.trim(),
            });
            setCompanyName(trimmed);
            toast.showSuccess('Company details saved');
        } catch (error) {
            toast.showApiError(error, 'Failed to save company details');
        } finally {
            setSavingName(false);
        }
    };

    const handleSave = async () => {
        if (!institutionId) {
            toast.showError('No institution selected');
            return;
        }
        if (weekendDays.length === 0) {
            toast.showError('Select at least one weekend day');
            return;
        }
        if (weekendDays.length >= 7) {
            toast.showError('Weekend cannot cover all 7 days');
            return;
        }
        try {
            setSaving(true);
            await InstitutionSettingsService.updateWeekendDays(institutionId, weekendDays);
            toast.showSuccess('Institution settings saved');
        } catch (error) {
            toast.showApiError(error, 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveTwoFactorPolicy = async () => {
        if (!institutionId) {
            toast.showError('No institution selected');
            return;
        }
        try {
            setSavingTwoFactor(true);
            await InstitutionSettingsService.updateTwoFactorPolicy(institutionId, twoFactorPolicy);
            toast.showSuccess('Two-factor authentication policy saved');
        } catch (error) {
            toast.showApiError(error, 'Failed to save two-factor authentication policy');
        } finally {
            setSavingTwoFactor(false);
        }
    };

    const handleSaveLocationPolicy = async () => {
        if (!institutionId) {
            toast.showError('No institution selected');
            return;
        }
        if (
            locationMode === 'FLAG' &&
            (!Number.isInteger(thresholdMeters) || thresholdMeters < 20 || thresholdMeters > 2000)
        ) {
            toast.showError('Accuracy threshold must be a whole number between 20 and 2000 metres');
            return;
        }
        try {
            setSavingLocation(true);
            await InstitutionSettingsService.updateLocationPolicy(
                institutionId,
                locationMode,
                locationMode === 'FLAG' ? thresholdMeters : undefined
            );
            toast.showSuccess('Location policy saved');
        } catch (error) {
            toast.showApiError(error, 'Failed to save location policy');
        } finally {
            setSavingLocation(false);
        }
    };

    if (loading) {
        return <Box sx={{ p: 3 }}><Typography>Loading...</Typography></Box>;
    }

    if (!institutionId) {
        return <Box sx={{ p: 3 }}><Typography>No institution selected</Typography></Box>;
    }

    return (
        <Box sx={{ p: 3 }} data-help-page="institutions.settings.page">
            <Typography variant="h5" sx={{ mb: 3 }}>Institution Settings</Typography>

            {companyCode !== null && (
                <Paper sx={{ p: 3, mb: 3 }} data-help-section="institutions.settings.section.company">
                    <Typography variant="h6" sx={{ mb: 2 }}>Company Details</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Shown in the header and on letters, payslips and statutory registers.
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                        <TextField
                            label="Company name"
                            data-help="institutions.settings.field.companyName"
                            size="small"
                            required
                            value={companyName}
                            onChange={e => setCompanyName(e.target.value)}
                            sx={{ flex: '1 1 320px', maxWidth: 520 }}
                        />
                        <TextField
                            label="Address"
                            data-help="institutions.settings.field.address"
                            size="small"
                            value={companyAddress}
                            onChange={e => setCompanyAddress(e.target.value)}
                            sx={{ flex: '2 1 420px', maxWidth: 720 }}
                        />
                        <TextField
                            label="TIN / BIN"
                            data-help="institutions.settings.field.tinBin"
                            size="small"
                            value={companyTinBin}
                            onChange={e => setCompanyTinBin(e.target.value)}
                            sx={{ flex: '1 1 200px', maxWidth: 280 }}
                        />
                        <Button variant="contained" onClick={handleSaveName} disabled={savingName} data-help="institutions.settings.saveCompany">
                            {savingName ? 'Saving...' : 'Save Company Details'}
                        </Button>
                    </Box>
                </Paper>
            )}

            <Paper sx={{ p: 3 }} data-help-section="institutions.settings.section.weekend">
                <Typography variant="h6" sx={{ mb: 2 }}>Weekend Days</Typography>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Days marked here are treated as weekends in attendance reports and payroll calculations.
                </Typography>

                <ToggleButtonGroup
                    value={weekendDays}
                    onChange={(_, next: WeekDay[]) => setWeekendDays(next)}
                    aria-label="weekend days"
                    data-help="institutions.settings.field.weekendDays"
                    size="small"
                    sx={{ mb: 3 }}
                >
                    {WEEK_DAYS.map(d => (
                        <ToggleButton key={d.value} value={d.value} aria-label={d.value}>
                            {d.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>

                <Box>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        data-help="institutions.settings.saveWeekend"
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save Settings'}
                    </Button>
                </Box>
            </Paper>

            <Paper sx={{ p: 3, mt: 3 }} data-help-section="institutions.settings.section.twoFactor">
                <Typography variant="h6" sx={{ mb: 2 }}>Two-Factor Authentication</Typography>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    When required, every user in this institution must set up an authenticator app before they can log in.
                </Typography>

                <FormControlLabel
                    data-help="institutions.settings.field.requireTwoFactor"
                    control={
                        <Checkbox
                            checked={twoFactorPolicy === 'REQUIRED'}
                            onChange={(e) => setTwoFactorPolicy(e.target.checked ? 'REQUIRED' : 'OPTIONAL')}
                        />
                    }
                    label="Require two-factor authentication for all users"
                    sx={{ mb: 2 }}
                />

                <Box>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveTwoFactorPolicy}
                        data-help="institutions.settings.saveTwoFactor"
                        disabled={savingTwoFactor}
                    >
                        {savingTwoFactor ? 'Saving...' : 'Save Two-Factor Settings'}
                    </Button>
                </Box>
            </Paper>

            <Paper sx={{ p: 3, mt: 3 }} data-help-section="institutions.settings.section.gps">
                <Typography variant="h6" sx={{ mb: 2 }}>GPS Attendance</Typography>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Punch locations are recorded and out-of-area punches are flagged for review — punches are never blocked.
                    Location can be faked in a browser; the mobile app additionally reports a mock-location warning.
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    If no work sites are configured, turning this on has no visible effect yet — add sites on the Work Sites page first.
                </Typography>

                <FormControlLabel
                    data-help="institutions.settings.field.gpsMode"
                    control={
                        <Switch
                            checked={locationMode === 'FLAG'}
                            onChange={(e) => setLocationMode(e.target.checked ? 'FLAG' : 'OFF')}
                        />
                    }
                    label="GPS attendance"
                    sx={{ mb: 2 }}
                />

                {locationMode === 'FLAG' && (
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            label="Accuracy threshold (metres)"
                            data-help="institutions.settings.field.gpsThreshold"
                            type="number"
                            size="small"
                            value={thresholdMeters}
                            onChange={(e) => setThresholdMeters(Number(e.target.value))}
                            inputProps={{ min: 20, max: 2000, step: 1 }}
                            error={!Number.isInteger(thresholdMeters) || thresholdMeters < 20 || thresholdMeters > 2000}
                            helperText="GPS readings less precise than this are recorded as low-accuracy rather than judged in or out of the site (20–2000 metres)."
                        />
                    </Box>
                )}

                <Box>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveLocationPolicy}
                        data-help="institutions.settings.saveLocation"
                        disabled={savingLocation}
                    >
                        {savingLocation ? 'Saving...' : 'Save location policy'}
                    </Button>
                </Box>
            </Paper>

            {institutionId && <ModuleAccessCard institutionId={institutionId} />}
            {institutionId && <ApprovalControlsCard institutionId={institutionId} />}
            {institutionId && <UsageLimitsCard institutionId={institutionId} />}
        </Box>
    );
};

export default InstitutionSettingsPage;
